/** EPBCS — Workflow assignment setup (Owner / Alt Owner / Reviewer per form × POV). */
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash, PencilSimple } from "@phosphor-icons/react";
import { Field, Input, Select, Card, Btn } from "./common";
import { useDims } from "./povUtils";

const SECTIONS = [
  ["revenue", "Revenue Budget"], ["opex", "OpEx Budget"], ["capex", "CapEx Budget"],
  ["workforce", "Workforce Budget"], ["financials", "Financial Statements"],
];
const EMPTY = { section: "revenue", entity: "", division: "", scenario: "", version: "",
  owner: "", alt_owner: "", reviewer: "" };

export default function WorkflowSetupTab() {
  const dims = useDims();
  const [rows, setRows] = useState([]);
  const [draft, setDraft] = useState(EMPTY);

  const load = () => api.get("/epbcs/workflow-assignments").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      await api.post("/epbcs/workflow-assignments", draft);
      toast.success(draft.id ? "Assignment updated" : "Assignment created");
      setDraft(EMPTY);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save assignment");
    }
  };
  const del = async (id) => { await api.delete(`/epbcs/workflow-assignments/${id}`); load(); };

  const povSelect = (k, label) => (
    <Field label={`${label} (blank = all)`}>
      <Select data-testid={`wfs-${k}`} value={draft[k]}
        onChange={(e) => setDraft({ ...draft, [k]: e.target.value })}>
        <option value="">— All —</option>
        {(dims?.[k === "entity" ? "entity" : k] || []).map((d) =>
          <option key={d.code} value={d.code}>{d.code} — {d.name}</option>)}
      </Select>
    </Field>
  );

  return (
    <div className="space-y-4" data-testid="workflow-setup-tab">
      <Card title={draft.id ? "Edit assignment" : "Assign owners & reviewers"}
        subtitle="Each budgeting form can have an Owner, Alternate Owner and Reviewer per Entity / Division / Scenario / Version. Assigned forms can only be promoted by their owner and approved by their reviewer."
        testid="wfs-editor">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Field label="Budgeting Form *">
            <Select data-testid="wfs-section" value={draft.section}
              onChange={(e) => setDraft({ ...draft, section: e.target.value })}>
              {SECTIONS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </Select>
          </Field>
          {povSelect("entity", "Entity")}
          {povSelect("division", "Division")}
          {povSelect("scenario", "Scenario")}
          {povSelect("version", "Version")}
          <Field label="Owner (email) *">
            <Input data-testid="wfs-owner" placeholder="owner@company.com" value={draft.owner}
              onChange={(e) => setDraft({ ...draft, owner: e.target.value })} />
          </Field>
          <Field label="Alternate Owner (email)">
            <Input data-testid="wfs-alt-owner" placeholder="alt@company.com" value={draft.alt_owner}
              onChange={(e) => setDraft({ ...draft, alt_owner: e.target.value })} />
          </Field>
          <Field label="Reviewer (email) *">
            <Input data-testid="wfs-reviewer" placeholder="reviewer@company.com" value={draft.reviewer}
              onChange={(e) => setDraft({ ...draft, reviewer: e.target.value })} />
          </Field>
        </div>
        <div className="mt-3 flex justify-end gap-2">
          {draft.id && (
            <button onClick={() => setDraft(EMPTY)}
              className="border border-slate-300 text-slate-600 px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider rounded">
              Cancel
            </button>
          )}
          <Btn onClick={save} data-testid="wfs-save" disabled={!draft.owner || !draft.reviewer}>
            <Plus size={12} className="inline mr-1" />{draft.id ? "Update" : "Save assignment"}
          </Btn>
        </div>
      </Card>

      <Card title={`Assignments (${rows.length})`} testid="wfs-list">
        <table className="text-[12px] w-full">
          <thead>
            <tr className="text-left text-[9px] tracking-[0.12em] uppercase text-slate-500 border-b border-slate-200">
              <th className="py-2">Form</th><th className="py-2">Entity</th><th className="py-2">Division</th>
              <th className="py-2">Scenario</th><th className="py-2">Version</th>
              <th className="py-2">Owner</th><th className="py-2">Alt Owner</th><th className="py-2">Reviewer</th>
              <th className="py-2 w-14" />
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={9} className="py-5 text-center text-slate-400">
                No assignments yet — forms fall back to role-based workflow (preparers promote, reviewers approve).
              </td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.id} className="border-b border-slate-100" data-testid={`wfs-row-${r.section}`}>
                <td className="py-1.5 font-semibold">{SECTIONS.find(([k]) => k === r.section)?.[1] || r.section}</td>
                {["entity", "division", "scenario", "version"].map((k) =>
                  <td key={k} className="py-1.5">{r[k] || <span className="text-slate-300">All</span>}</td>)}
                <td className="py-1.5">{r.owner}</td>
                <td className="py-1.5">{r.alt_owner || "—"}</td>
                <td className="py-1.5">{r.reviewer}</td>
                <td className="py-1.5">
                  <div className="flex items-center gap-2">
                    <button onClick={() => setDraft({ ...EMPTY, ...r })} data-testid={`wfs-edit-${r.section}`}
                      className="text-slate-400 hover:text-[#0B1E3F]"><PencilSimple size={13} /></button>
                    <button onClick={() => del(r.id)} data-testid={`wfs-delete-${r.section}`}
                      className="text-slate-400 hover:text-red-600"><Trash size={13} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
